import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { TrendingUp, TrendingDown, DollarSign, Calendar, Repeat, AlertCircle } from "lucide-react";

interface Client {
  id: string;
  name: string;
  email: string;
}

interface Case {
  id: string;
  title: string;
  case_number?: string;
}

interface RecurringEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  due_day: number;
  is_active: boolean;
  created_at: string;
  client_id?: string;
  case_id?: string;
}

interface RecurringEntryFormProps {
  isOpen: boolean;
  onClose: () => void;
  editingEntry: RecurringEntry | null;
  clients: Client[];
  cases: Case[];
  onSave: () => void;
  dialogType?: "receita" | "despesa" | "general";
}

const RecurringEntryForm = ({ 
  isOpen, 
  onClose, 
  editingEntry, 
  clients, 
  cases, 
  onSave,
  dialogType = "general"
}: RecurringEntryFormProps) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    description: editingEntry?.description || "",
    amount: editingEntry?.amount.toString() || "",
    entry_type: editingEntry?.entry_type || dialogType,
    category: editingEntry?.category || "",
    due_day: editingEntry?.due_day?.toString() || "5",
    is_active: editingEntry?.is_active ?? true,
    client_id: editingEntry?.client_id || "",
    case_id: editingEntry?.case_id || ""
  });

  // Categorias específicas para cada tipo
  const revenueCategories = [
    "Assessoria Jurídica",
    "Consultoria Jurídica", 
    "Honorários", 
    "Consulta", 
    "Processo", 
    "Audiência", 
    "Petição", 
    "Recurso", 
    "Protocolo", 
    "Outros"
  ];
  
  const expenseCategories = [
    "Escritório", 
    "Cartório", 
    "Transporte", 
    "Marketing", 
    "Impostos", 
    "Seguros", 
    "Equipamentos", 
    "Aluguel",
    "Contas",
    "Internet",
    "Telefone",
    "Energia",
    "Água",
    "Outros"
  ];

  // Atualizar formData quando dialogType ou editingEntry mudar
  useEffect(() => {
    if (editingEntry) {
      setFormData({
        description: editingEntry.description,
        amount: editingEntry.amount.toString(),
        entry_type: editingEntry.entry_type,
        category: editingEntry.category,
        due_day: editingEntry.due_day.toString(),
        is_active: editingEntry.is_active,
        client_id: editingEntry.client_id || "",
        case_id: editingEntry.case_id || ""
      });
    } else {
      setFormData({
        description: "",
        amount: "",
        entry_type: dialogType,
        category: "",
        due_day: "5",
        is_active: true,
        client_id: "",
        case_id: ""
      });
    }
  }, [editingEntry, dialogType]);

  const resetForm = () => {
    setFormData({
      description: "",
      amount: "",
      entry_type: dialogType,
      category: "",
      due_day: "5",
      is_active: true,
      client_id: "",
      case_id: ""
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const dataToSave = {
        description: formData.description,
        amount: parseFloat(formData.amount),
        entry_type: formData.entry_type,
        category: formData.category,
        due_day: parseInt(formData.due_day),
        is_active: formData.is_active,
        client_id: formData.client_id === "none" ? null : formData.client_id || null,
        case_id: formData.case_id === "none" ? null : formData.case_id || null,
        user_id: user.id
      };

      if (editingEntry) {
        const { error } = await supabase
          .from('recurring_entries' as any)
          .update(dataToSave)
          .eq('id', editingEntry.id);

        if (error) throw error;
        toast({
          title: "Sucesso",
          description: "Entrada recorrente atualizada com sucesso",
        });
      } else {
        const { error } = await supabase
          .from('recurring_entries' as any)
          .insert(dataToSave);

        if (error) throw error;
        toast({
          title: "Sucesso",
          description: "Entrada recorrente criada com sucesso",
        });
      }

      onSave();
      onClose();
      resetForm();
    } catch (error) {
      console.error('Erro ao salvar entrada recorrente:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar a entrada recorrente",
        variant: "destructive"
      });
    }
  };

  const getDialogTitle = () => {
    if (editingEntry) {
      return `Editar ${editingEntry.entry_type === 'receita' ? 'Receita' : 'Despesa'} Recorrente`;
    }
    return dialogType === 'receita' ? 'Nova Receita Recorrente' : dialogType === 'despesa' ? 'Nova Despesa Recorrente' : 'Nova Entrada Recorrente';
  };

  const getDialogIcon = () => {
    if (editingEntry) {
      return editingEntry.entry_type === 'receita' ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />;
    }
    return dialogType === 'receita' ? <TrendingUp className="w-5 h-5" /> : dialogType === 'despesa' ? <TrendingDown className="w-5 h-5" /> : <DollarSign className="w-5 h-5" />;
  };

  const getCategoryOptions = () => {
    return formData.entry_type === 'receita' ? revenueCategories : expenseCategories;
  };

  const getDueDayOptions = () => {
    return Array.from({ length: 31 }, (_, i) => i + 1);
  };

  const getDueDayLabel = () => {
    return formData.entry_type === 'receita' ? 'Data de Recebimento' : 'Data de Vencimento';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden border border-[#c4ab83]/30 bg-white shadow-2xl">
        <DialogHeader className="relative -m-6 mb-2 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
          {/* Monograma AS como marca d'água */}
          <img
            src="/imgs/logos allana/letra_A_logo.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]"
          />
          <div className="relative flex items-center gap-4 text-left">
            <img
              src="/imgs/logos allana/letra_A_logo.png"
              alt="Allana Sthéfany"
              className="h-12 w-12 shrink-0 select-none object-contain opacity-80"
            />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Recorrente · todo mês</p>
              <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">{getDialogTitle()}</DialogTitle>
            </div>
          </div>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="description" className="block text-sm font-medium text-slate-700">
                Descrição *
              </Label>
              <Input
                id="description"
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição da entrada recorrente"
                required
                className="w-full rounded-lg border-slate-200 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="entry_type" className="block text-sm font-medium text-slate-700">
                Tipo *
              </Label>
              <Select 
                value={formData.entry_type} 
                onValueChange={(value) => {
                  setFormData({ 
                    ...formData, 
                    entry_type: value,
                    category: "" // Reset category when type changes
                  });
                }}
              >
                <SelectTrigger className="w-full rounded-lg border-slate-200 text-sm text-slate-900 shadow-none">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent className="bg-white border-2 border-gray-200">
                  <SelectItem value="receita" className="text-gray-900 hover:bg-blue-50">Receita</SelectItem>
                  <SelectItem value="despesa" className="text-gray-900 hover:bg-blue-50">Despesa</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount" className="block text-sm font-medium text-slate-700">
                Valor Mensal *
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0,00"
                required
                className="w-full rounded-lg border-slate-200 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category" className="block text-sm font-medium text-slate-700">
                Categoria *
              </Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="w-full rounded-lg border-slate-200 text-sm text-slate-900 shadow-none">
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent className="bg-white border-2 border-gray-200">
                  {getCategoryOptions().map((category) => (
                    <SelectItem key={category} value={category} className="text-gray-900 hover:bg-blue-50">
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="due_day" className="block text-sm font-medium text-slate-700">
                {getDueDayLabel()} *
              </Label>
              <Select 
                value={formData.due_day} 
                onValueChange={(value) => setFormData({ ...formData, due_day: value })}
              >
                <SelectTrigger className="w-full rounded-lg border-slate-200 text-sm text-slate-900 shadow-none">
                  <SelectValue placeholder="Selecione o dia" />
                </SelectTrigger>
                <SelectContent className="bg-white border-2 border-gray-200">
                  {getDueDayOptions().map((day) => (
                    <SelectItem key={day} value={day.toString()} className="text-gray-900 hover:bg-blue-50">
                      Dia {day}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_id" className="block text-sm font-medium text-slate-700">
                Cliente
              </Label>
              <Select 
                value={formData.client_id} 
                onValueChange={(value) => setFormData({ ...formData, client_id: value })}
              >
                <SelectTrigger className="w-full rounded-lg border-slate-200 text-sm text-slate-900 shadow-none">
                  <SelectValue placeholder="Selecione um cliente" />
                </SelectTrigger>
                <SelectContent className="bg-white border-2 border-gray-200">
                  <SelectItem value="none" className="text-gray-900 hover:bg-blue-50">Nenhum cliente</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id} className="text-gray-900 hover:bg-blue-50">
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="case_id" className="block text-sm font-medium text-slate-700">
                Processo Relacionado
              </Label>
              <Select 
                value={formData.case_id} 
                onValueChange={(value) => setFormData({ ...formData, case_id: value })}
              >
                <SelectTrigger className="w-full rounded-lg border-slate-200 text-sm text-slate-900 shadow-none">
                  <SelectValue placeholder="Selecione um processo" />
                </SelectTrigger>
                <SelectContent className="bg-white border-2 border-gray-200">
                  <SelectItem value="none" className="text-gray-900 hover:bg-blue-50">Nenhum processo</SelectItem>
                  {cases.map((case_) => (
                    <SelectItem key={case_.id} value={case_.id} className="text-gray-900 hover:bg-blue-50">
                      {case_.title} {case_.case_number && `(${case_.case_number})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="is_active" className="block text-sm font-medium text-slate-700">
                Status
              </Label>
              <div className="flex items-center space-x-3 pt-2">
                <Checkbox
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked as boolean })}
                  className="border-2 border-gray-300 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                />
                <Label htmlFor="is_active" className="text-sm font-semibold text-gray-800 cursor-pointer">
                  Entrada Ativa
                </Label>
              </div>
            </div>
          </div>

          {/* Como funciona */}
          <div className="flex items-start gap-3 rounded-xl bg-[#faf7f2] px-4 py-3 text-xs text-slate-600">
            <Repeat className="mt-0.5 h-4 w-4 shrink-0 text-[#9a7f52]" />
            <p>
              O valor se repete <strong className="text-slate-800">todo mês</strong> no dia escolhido. Desmarque <strong className="text-slate-800">Entrada ativa</strong> para pausar sem apagar.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 border-t border-slate-100 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
              Cancelar
            </Button>
            <Button type="submit" className="rounded-xl bg-[#c4ab83] px-6 font-semibold text-slate-900 hover:bg-[#b39a72]">
              {editingEntry ? 'Salvar alterações' : 'Criar recorrente'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RecurringEntryForm; 